import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AuditTrail, BulkActionBar, ColumnPicker, ConfirmDialog, DataTable, DateRangePicker,
  DecisionPanel, DensityToggle, EmptyState, exportFilename, exportRows, FilterBar,
  formatThaiDate, ImageCompare, ImageViewer, KeyboardShortcutHelp, Pagination, QUEUE_SHORTCUTS,
  SearchInput, SideDrawer, SourceBadge, SplitView, StatusBadge, Toolbar, useTableDensity, useToast,
  type ActiveFilter, type AuditEntry, type Column, type DateRange, type QueueStatus, type RejectReason,
  type SortState,
} from '../components';
import { Icon } from '../components/base/Icon';
import {
  AUTO_FAILURE_LABEL, CLAIMS, CURRENT_STAFF_ID, PLATFORM_ORDER_URL,
  SLA_HOURS, SLA_WARN_HOURS, STAFF, auditFor, pointsFor,
  type Channel, type Claim, type ReceiptScan,
} from './mockData';

/**
 * ClaimQueueScreen — คิวตรวจสอบคำขอสะสมคะแนน (FR-ADM-10 ถึง 14)
 *
 * หน้าที่เจ้าหน้าที่ใช้ทั้งวัน วัดความสำเร็จที่ "ตรวจได้กี่รายการต่อชั่วโมง"
 * ทุกการตัดสินใจในไฟล์นี้ตัดสินจากคำถามนั้น:
 *
 *   1 รายการที่ยอดถูกต้อง = การกด 1 ครั้ง (ปุ่มอนุมัติ หรือคีย์ A)
 *   ตัดสินแล้วเลื่อนไปรายการถัดไปเอง ไม่ต้องกลับไปคลิกที่รายการ
 *   ภาพสลิปของรายการถัดไปโหลดล่วงหน้าเสมอ
 *   เปลี่ยนรายการไม่โหลดหน้าใหม่ (SplitView)
 *
 * สลิปกับ Order ID อยู่คิวเดียวกัน แยกด้วยตัวกรอง "ที่มา" เพราะเมื่อเชื่อม API
 * ของแพลตฟอร์มสำเร็จ คำขอจาก Order ID จะหายจากคิวคนไปเอง (PROGRESS-REPORT 3.1)
 * ถ้าแยกเป็นสองเมนู เมนูหนึ่งจะกลายเป็นเมนูว่างที่ยังต้องดูแล
 */

type StatusTab = 'pending' | 'approved' | 'rejected' | 'all';
type ScreenState = 'ready' | 'loading' | 'error';

/**
 * ช่องทางที่พบบ่อย ใช้จัดลำดับให้มาร์เก็ตเพลสขึ้นก่อนเสมอ
 *
 * ⚠️ **ไม่ใช่รายการทั้งหมด** ช่องทางจริงมาจาก `order_location` ซึ่งเป็นข้อความอิสระ
 *    ที่ลูกค้าเลือกในแอป ตัวเลือกในตัวกรองจึงต้องสร้างจากข้อมูลที่โหลดมาจริง
 *    ไม่งั้นช่องทางอย่าง "งานออกบูธ" จะไม่มีให้เลือกทั้งที่มีคำขออยู่
 */
const CHANNEL_ORDER: Channel[] = ['Shopee', 'Lazada', 'TikTok Shop'];

const REJECT_REASONS: RejectReason[] = [
  { code: 'unreadable', label: 'รูปสลิปไม่ชัด อ่านยอดเงินหรือวันที่ไม่ได้ กรุณาถ่ายใหม่ให้เห็นยอดและวันที่ครบ' },
  { code: 'amount-mismatch', label: 'ยอดในสลิปไม่ตรงกับยอดที่กรอก กรุณาส่งคำขอใหม่โดยกรอกยอดตามสลิป', requiresDetail: true },
  { code: 'duplicate', label: 'สลิปนี้ถูกใช้สะสมคะแนนไปแล้ว ถ้าเป็นการซื้อครั้งใหม่ กรุณาแนบสลิปของครั้งนั้น' },
  { code: 'not-innobic', label: 'ใบเสร็จนี้ไม่มีสินค้าอินโนบิก คะแนนสะสมได้จากสินค้าอินโนบิกเท่านั้น', requiresDetail: true },
  { code: 'out-of-window', label: 'วันที่ในสลิปเกินกำหนดส่งคำขอ (ภายใน 30 วันหลังซื้อ)' },
  { code: 'other', label: 'เหตุผลอื่น', requiresDetail: true },
];

/* ------------------------------------------------------------------ ตัวช่วย */

/** อายุคำขอเป็นข้อความ + ป้าย SLA — ป้ายเป็นข้อความ ไม่ใช่แค่สี */
function slaLabel(ageHours: number): { text: string; tone: 'ok' | 'warn' | 'over' } {
  const remaining = SLA_HOURS - ageHours;
  if (remaining < 0) return { text: `เกิน SLA ${Math.floor(-remaining / 24)} วัน`, tone: 'over' };
  if (ageHours >= SLA_WARN_HOURS) return { text: `เหลือ ${remaining} ชม.`, tone: 'warn' };
  return { text: `เหลือ ${Math.floor(remaining / 24)} วัน`, tone: 'ok' };
}

const ageText = (h: number) => (h < 24 ? `${h} ชม.` : `${Math.floor(h / 24)} วัน ${h % 24} ชม.`);

const SlaTag: React.FC<{ ageHours: number }> = ({ ageHours }) => {
  const { text, tone } = slaLabel(ageHours);
  const style =
    tone === 'over'
      ? 'border-[var(--status-overdue-border)] bg-[var(--status-overdue-bg)] text-[var(--status-overdue-text)]'
      : tone === 'warn'
        ? 'border-[var(--warning-border)] bg-[var(--warning-bg)] text-[var(--warning-text)]'
        : 'border-[var(--line-200)] bg-[var(--surface-100)] text-[var(--ink-700)]';
  return (
    <span className={`inline-flex items-center gap-1 rounded-[var(--radius-sm)] border px-[var(--space-15)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)] ${style}`}>
      {tone !== 'ok' && <Icon name={tone === 'over' ? 'running_with_errors' : 'schedule'} size={13} filled />}
      {text}
    </span>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({ label, children, hint }) => (
  <div className="flex flex-col gap-[var(--space-05)]">
    <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">{label}</span>
    <span className="text-[length:var(--text-form-lg)] leading-[var(--leading-form)] font-[var(--weight-medium)] text-[var(--ink-900)]">
      {children}
    </span>
    {hint && <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">{hint}</span>}
  </div>
);

/* --------------------------------------------------- รายการที่ระบบอ่านจากสลิป */

/** แสดงก่อนกี่บรรทัด — ใบเสร็จยาวไม่ควรดันข้อมูลที่ต้องใช้ตัดสินตกจอ */
const SCAN_PREVIEW_LINES = 5;

const baht = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * ผล OCR ของสลิป — ให้เจ้าหน้าที่เทียบกับภาพได้โดยไม่ต้องซูมไล่อ่านทีละบรรทัด
 *
 * ⚠️ เป็นค่าที่เครื่องอ่าน ไม่ใช่ความจริง — หัวข้อต้องบอกเสมอว่าอาจผิด
 *    และไม่มีปุ่มเติมยอดให้อัตโนมัติ (ตกลงกันแล้ว) เจ้าหน้าที่ต้องพิมพ์เองหลังดูภาพ
 */
const ReceiptScanSection: React.FC<{ scan: ReceiptScan }> = ({ scan }) => {
  const [expanded, setExpanded] = useState(false);
  const itemsTotal = scan.items.reduce((s, i) => s + i.amount, 0);
  const shown = expanded ? scan.items : scan.items.slice(0, SCAN_PREVIEW_LINES);
  const hidden = scan.items.length - shown.length;
  /* ทศนิยมสองตำแหน่ง — ต่างไม่เกิน 1 สตางค์ถือว่าเท่ากัน */
  const totalDiffers = scan.total !== null && scan.items.length > 0 && Math.abs(scan.total - itemsTotal) > 0.01;

  return (
    <section aria-label="รายการที่ระบบอ่านจากสลิป" className="rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-3)]">
      <h3 className="mb-[var(--space-1)] flex items-center gap-[var(--space-1)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--ink-700)]">
        <Icon name="receipt_long" size={16} />
        รายการที่ระบบอ่านจากสลิป
      </h3>
      <p className="mb-[var(--space-2)] flex items-center gap-[var(--space-1)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">
        <Icon name="info" size={14} />
        อ่านอัตโนมัติ อาจผิด — เทียบกับภาพสลิปทุกครั้ง
      </p>

      {scan.items.length === 0 ? (
        <p className="text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
          ระบบอ่านรายการสินค้าจากสลิปนี้ไม่ได้
          {scan.total !== null && <> · ยอดสุทธิที่อ่านได้ <span className="bo-num">{baht(scan.total)}</span> บาท</>}
        </p>
      ) : (
        <table className="w-full text-[length:var(--text-table-sm)] leading-[var(--leading-table)]">
          <caption className="sr-only">รายการสินค้าที่ระบบอ่านจากสลิป</caption>
          <thead>
            <tr className="text-left text-[var(--ink-600)]">
              <th scope="col" className="py-[var(--space-05)] pr-[var(--space-2)] font-[var(--weight-medium)]">รายการ</th>
              <th scope="col" className="py-[var(--space-05)] pr-[var(--space-2)] text-right font-[var(--weight-medium)]">จำนวน</th>
              <th scope="col" className="py-[var(--space-05)] text-right font-[var(--weight-medium)]">ราคารวม (บาท)</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((item, idx) => (
              <tr key={`${idx}-${item.name}`} className="border-t border-[var(--line-200)] align-top">
                <td className="py-[var(--space-1)] pr-[var(--space-2)] text-[var(--ink-900)]">{item.name}</td>
                <td className="bo-num py-[var(--space-1)] pr-[var(--space-2)] text-[var(--ink-900)]">{item.qty.toLocaleString('th-TH')}</td>
                <td className="bo-num py-[var(--space-1)] text-[var(--ink-900)]">{baht(item.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-[var(--line-300)]">
              <th scope="row" colSpan={2} className="py-[var(--space-1)] pr-[var(--space-2)] text-left font-[var(--weight-medium)] text-[var(--ink-700)]">
                รวมจากรายการ{hidden > 0 ? ` (ทั้ง ${scan.items.length.toLocaleString('th-TH')} บรรทัด)` : ''}
              </th>
              <td className="bo-num py-[var(--space-1)] font-[var(--weight-medium)] text-[var(--ink-900)]">{baht(itemsTotal)}</td>
            </tr>
            {scan.total !== null && (
              <tr>
                <th scope="row" colSpan={2} className="py-[var(--space-05)] pr-[var(--space-2)] text-left font-[var(--weight-medium)] text-[var(--ink-700)]">
                  ยอดสุทธิที่อ่านได้
                </th>
                <td className="bo-num py-[var(--space-05)] font-[var(--weight-semibold)] text-[var(--ink-900)]">{baht(scan.total)}</td>
              </tr>
            )}
          </tfoot>
        </table>
      )}

      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-[var(--space-1)] inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-1)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--blue-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
        >
          <Icon name="expand_more" size={16} />
          ดูทั้งหมดอีก {hidden.toLocaleString('th-TH')} บรรทัด
        </button>
      )}

      {totalDiffers && (
        <p className="mt-[var(--space-2)] flex items-start gap-[var(--space-1)] rounded-[var(--radius-sm)] border border-[var(--warning-border)] bg-[var(--warning-bg)] p-[var(--space-2)] text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--warning-text)]">
          <Icon name="warning" size={14} filled />
          <span>
            ยอดสุทธิไม่เท่ารวมจากรายการ (ต่างกัน <span className="bo-num">{baht(Math.abs(scan.total! - itemsTotal))}</span> บาท)
            — อาจมีส่วนลดท้ายบิล หรือระบบอ่านบางบรรทัดผิด
          </span>
        </p>
      )}
    </section>
  );
};

/* --------------------------------------------------- แผงรายละเอียดฝั่งขวา */

/** ความยาวขั้นต่ำของเหตุผลที่แก้ยอด — กันการพิมพ์แค่จุดเดียวให้ผ่าน */
const AMEND_REASON_MIN = 5;

interface DetailProps {
  claim: Claim;
  nextSlipUrl?: string;
  /** comment = เหตุผลที่แก้ยอด — ส่งเมื่อยอดต่างจากที่ลูกค้ากรอก (backend บังคับ) */
  onApprove: (claim: Claim, amount: number, comment?: string) => void;
  onReject: (claim: Claim, payload: { code: string; label: string; detail: string }) => void;
  onOpenAudit: () => void;
  /** ประวัติของคำขอนี้ — ชั้นบนเป็นคนเลือกว่ามาจากของจริงหรือข้อมูลจำลอง */
  auditEntries: AuditEntry[];
  busy: boolean;
  /** สูตรคำนวณคะแนน — ส่งต่อมาจากหน้าจอหลัก ดูเหตุผลที่ prop pointsForAmount */
  calcPoints: (amount: number) => number;
  /** ปิดแผงรายละเอียดแล้วกลับไปดูตารางเต็มความกว้าง */
  onClose: () => void;
  /** แสดงปุ่มประวัติการแก้ไขไหม — ปิดเมื่อ backend ยังไม่มี endpoint ให้ */
  showAudit: boolean;
  /** แสดงป้ายที่มา (ระบบตรวจ/คนตรวจ) ไหม — ปิดพร้อมคอลัมน์ "ที่มา" ให้สอดคล้องกัน */
  showSource: boolean;
  /** แหล่งข้อมูลมีระดับสมาชิก คะแนนคงเหลือ และสถิติการส่งคำขอจริงหรือไม่ */
  showMemberStats: boolean;
}

const ClaimDetail: React.FC<DetailProps> = ({
  claim, nextSlipUrl, onApprove, onReject, onOpenAudit, busy, calcPoints, onClose, showAudit, showSource,
  auditEntries, showMemberStats,
}) => {
  /**
   * อัตราแลกเปลี่ยนที่แสดง — คำนวณย้อนจากสูตรที่ใช้คิดคะแนนจริง
   *
   * ยิงยอดตัวอย่างเข้าสูตรแล้วหารกลับ จึงตรงกับที่ backend คิดเสมอ
   * ไม่ว่าอัตราจะมาจากข้อมูลจำลองหรือจาก earning policy ของ division
   */
  const rateLabel = useMemo(() => {
    const probe = 10_000;
    const pts = calcPoints(probe);
    if (!pts || pts <= 0) return 'ยังไม่ทราบอัตราแลกเปลี่ยน';
    return `${(probe / pts).toLocaleString('th-TH', { maximumFractionDigits: 2 })} บาท = 1 แต้ม`;
  }, [calcPoints]);
  const [amount, setAmount] = useState<number>(claim.verifiedAmount ?? claim.declaredAmount);
  const [compare, setCompare] = useState(false);
  /** เหตุผลที่แก้ยอด — บังคับเมื่อยอดต่างจากที่ลูกค้ากรอก (FR-ADM-04) */
  const [amendReason, setAmendReason] = useState('');

  /* เปลี่ยนรายการ = รีเซ็ตยอดที่แก้ไว้ ไม่ให้ยอดของรายการก่อนติดมา */
  const lastId = useRef(claim.id);
  if (lastId.current !== claim.id) {
    lastId.current = claim.id;
    setAmount(claim.verifiedAmount ?? claim.declaredAmount);
    setCompare(false);
    setAmendReason('');
  }

  const points = calcPoints(amount);
  const declaredPoints = calcPoints(claim.declaredAmount);
  const amended = amount !== claim.declaredAmount;
  const duplicate = claim.anomalies.find((a) => a.kind === 'duplicate-slip');
  const decided = claim.status === 'approved' || claim.status === 'rejected';
  const rate = claim.member.claimsTotal
    ? Math.round((claim.member.claimsApproved / claim.member.claimsTotal) * 100)
    : 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-[var(--blue-050)] font-[family-name:var(--font-thai)]">
      <header className="flex flex-wrap items-center gap-[var(--space-2)] border-b border-[var(--line-200)] bg-[var(--surface-0)] px-[var(--space-4)] py-[var(--space-25)]">
        <h2 className="bo-num text-[length:var(--text-section)] font-[var(--weight-semibold)] leading-[var(--leading-heading)] text-[var(--ink-900)]" style={{ textAlign: 'left' }}>
          {claim.code}
        </h2>
        <StatusBadge status={claim.status} size="md" />
        {showSource && <SourceBadge source={claim.source} />}
        <SlaTag ageHours={claim.ageHours} />
        {showAudit && (
        <button
          type="button"
          onClick={onOpenAudit}
          className="ml-auto inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-1)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-25)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
        >
          <Icon name="history" size={16} />
          ประวัติทั้งหมด
        </button>
        )}

        {/* ปุ่มปิดแผง — ไม่มีแล้วเปิดดูสลิปเสร็จจะกลับไปดูตารางเต็มความกว้างไม่ได้
            ⚠️ ตั้งใจไม่ผูกคีย์ Esc ที่นี่ เพราะ DecisionPanel ผูก Escape ไว้แล้ว
               สำหรับยกเลิกการปฏิเสธ ถ้าผูกซ้ำจะทำงานพร้อมกันทั้งสองอย่าง */}
        <button
          type="button"
          onClick={onClose}
          aria-label="ปิดแผงรายละเอียด"
          title="ปิดแผงรายละเอียด"
          className={`${showAudit ? '' : 'ml-auto '}inline-flex min-h-[var(--hit-control)] min-w-[var(--hit-control)] items-center justify-center rounded-[var(--radius-sm)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]`}
        >
          <Icon name="close" size={20} />
        </button>
      </header>

      {/* ซ้าย: ภาพสลิป (ตำแหน่งเด่นที่สุด) · ขวา: ข้อมูลที่กรอก อยู่ติดกันเพื่อเทียบไม่ต้องเลื่อนจอ */}
      <div className="grid min-h-0 flex-1 gap-[var(--space-3)] p-[var(--space-3)]" style={{ gridTemplateColumns: 'minmax(360px, 1.15fr) minmax(340px, 1fr)' }}>
        <div className="flex min-h-0 flex-col gap-[var(--space-2)]">
          {claim.kind === 'slip' && claim.slipUrl ? (
            compare && duplicate ? (
              <ImageCompare
                className="min-h-0 flex-1"
                left={{ src: claim.slipUrl, alt: `สลิปของคำขอ ${claim.code}`, caption: `${claim.code} — คำขอนี้` }}
                right={{ src: claim.slipUrl, alt: `สลิปของคำขอ ${duplicate.relatedClaimCode}`, caption: `${duplicate.relatedClaimCode} — อนุมัติไปแล้ว` }}
              />
            ) : (
              <ImageViewer
                className="min-h-0 flex-1"
                src={claim.slipUrl}
                preloadSrc={nextSlipUrl}
                alt={`ภาพสลิปของคำขอ ${claim.code}`}
                caption={`${claim.channel} · ส่งเมื่อ ${formatThaiDate(claim.submittedAt.slice(0, 10))} · ค้างมา ${ageText(claim.ageHours)}`}
                shortcutScope="zoom-only"
              />
            )
          ) : (
            /* คำขอจาก Order ID ไม่มีสลิป — แสดงเลขคำสั่งซื้อพร้อมปุ่มคัดลอกและลิงก์เปิดแพลตฟอร์ม */
            <div className="flex min-h-0 flex-1 flex-col items-start gap-[var(--space-3)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-5)]">
              <Field label="เลขคำสั่งซื้อ">
                <span className="bo-num text-[length:var(--text-title-sm)]" style={{ textAlign: 'left' }}>{claim.orderNumber}</span>
              </Field>
              <div className="flex flex-wrap gap-[var(--space-2)]">
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(claim.orderNumber ?? '')}
                  className="inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-1)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-3)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--ink-900)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
                >
                  <Icon name="content_copy" size={16} />
                  คัดลอกเลขคำสั่งซื้อ
                </button>
                {PLATFORM_ORDER_URL[claim.channel] && (
                  <a
                    href={`${PLATFORM_ORDER_URL[claim.channel]}${claim.orderNumber}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-1)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-3)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--blue-700)] no-underline hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
                  >
                    <Icon name="open_in_new" size={16} />
                    เปิดหน้าคำสั่งซื้อใน {claim.channel}
                  </a>
                )}
              </div>
              {claim.autoCheckFailure && (
                <div role="alert" className="flex items-start gap-[var(--space-2)] rounded-[var(--radius-sm)] border border-[var(--warning-border)] bg-[var(--warning-bg)] p-[var(--space-25)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--warning-text)]">
                  <Icon name="sync_problem" size={18} filled />
                  <span>ระบบตรวจอัตโนมัติไม่ผ่าน — {AUTO_FAILURE_LABEL[claim.autoCheckFailure]}</span>
                </div>
              )}
              <p className="text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
                เมื่อเชื่อม API ของ {claim.channel} สำเร็จ คำขอจากช่องทางนี้จะไม่ผ่านคิวคนอีก
                — เหลือเฉพาะรายการที่ระบบตรวจไม่ผ่านเท่านั้นที่มาถึงหน้านี้
              </p>
            </div>
          )}

          {duplicate && claim.kind === 'slip' && (
            <button
              type="button"
              onClick={() => setCompare((v) => !v)}
              className="inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-1)] self-start rounded-[var(--radius-sm)] border border-[var(--warning-border)] bg-[var(--warning-bg)] px-[var(--space-3)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--warning-text)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
            >
              <Icon name={compare ? 'close' : 'compare'} size={16} />
              {compare ? 'ปิดการเทียบภาพ' : `เทียบกับ ${duplicate.relatedClaimCode}`}
            </button>
          )}
        </div>

        <div className="flex min-h-0 flex-col gap-[var(--space-3)] overflow-auto">
          {claim.anomalies.length > 0 && (
            <section aria-label="สิ่งที่ระบบตรวจพบ" className="flex flex-col gap-[var(--space-15)] rounded-[var(--radius-md)] border border-[var(--warning-border)] bg-[var(--warning-bg)] p-[var(--space-3)]">
              <h3 className="flex items-center gap-[var(--space-1)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--warning-text)]">
                <Icon name="flag" size={18} filled />
                ระบบตรวจพบ {claim.anomalies.length} เรื่องที่ควรดูก่อนตัดสิน
              </h3>
              <ul className="flex flex-col gap-[var(--space-1)] pl-[var(--space-5)]" style={{ listStyle: 'disc' }}>
                {claim.anomalies.map((a) => (
                  <li key={a.kind} className="text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
                    {a.message}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {claim.kind === 'slip' && claim.receiptScan && (
            /* key — เปลี่ยนคำขอแล้วการกาง "ดูทั้งหมด" ไม่ติดไปคำขอถัดไป */
            <ReceiptScanSection key={claim.id} scan={claim.receiptScan} />
          )}

          <section aria-label="ข้อมูลที่ลูกค้ากรอก" className="rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-3)]">
            <h3 className="mb-[var(--space-2)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--ink-700)]">ข้อมูลที่ลูกค้ากรอก</h3>
            <div className="grid grid-cols-2 gap-[var(--space-3)]">
              <Field label="ช่องทาง">{claim.channel}</Field>
              <Field label="วันที่ส่งคำขอ">{formatThaiDate(claim.submittedAt.slice(0, 10))}</Field>
              <Field label="ยอดที่กรอก">
                <span className="bo-num" style={{ textAlign: 'left' }}>{claim.declaredAmount.toLocaleString('th-TH')} บาท</span>
              </Field>
              <Field label="คะแนนตามยอดที่กรอก">
                <span className="bo-num" style={{ textAlign: 'left' }}>{declaredPoints.toLocaleString('th-TH')} คะแนน</span>
              </Field>
            </div>

            {/* ช่องแก้ไขยอด — คะแนนคำนวณใหม่ทันทีให้เห็น */}
            <div className="mt-[var(--space-3)] flex flex-wrap items-end gap-[var(--space-3)] rounded-[var(--radius-sm)] bg-[var(--blue-050)] p-[var(--space-25)]">
              <label className="flex flex-col gap-[var(--space-1)]">
                <span className="text-[length:var(--text-table-sm)] text-[var(--ink-700)]">ยอดที่ใช้คำนวณคะแนน (บาท)</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={amount}
                  disabled={decided}
                  onChange={(e) => setAmount(Math.max(0, Number(e.target.value) || 0))}
                  className="bo-num min-h-[var(--hit-control)] w-[160px] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-2)] text-[length:var(--text-form-lg)] text-[var(--ink-900)] disabled:bg-[var(--bg-disabled)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
                />
              </label>
              <div className="flex flex-col gap-[var(--space-05)]">
                <span className="text-[length:var(--text-table-sm)] text-[var(--ink-700)]">คะแนนที่จะให้</span>
                <span aria-live="polite" className="bo-num text-[length:var(--text-title-sm)] font-[var(--weight-bold)] leading-[var(--leading-num)] text-[var(--blue-700)]" style={{ textAlign: 'left' }}>
                  {points.toLocaleString('th-TH')} คะแนน
                </span>
                {/*
                  ⚠️ เดิมเขียน `{BAHT_PER_POINT} บาท = 1 แต้ม` ซึ่งเป็นค่าคงที่ของข้อมูลจำลอง
                     ไม่ใช่อัตราจริงของ division ตอนต่อ CRM แล้วบังเอิญตรงกันพอดี
                     ป้ายจึงดูถูกทั้งที่ไม่ได้อ่านค่าจริง — ถ้า backend เปลี่ยนอัตราเมื่อไร
                     ป้ายนี้จะโกหกทันทีโดยไม่มีใครรู้ ทีนี้คำนวณย้อนจากสูตรที่ใช้จริง
                */}
                <span className="bo-num text-[length:var(--text-table-sm)] text-[var(--ink-600)]" style={{ textAlign: 'left' }}>
                  {rateLabel}
                </span>
              </div>
              {amended && (
                <p className="flex items-center gap-1 text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--warning-text)]">
                  <Icon name="edit" size={14} />
                  แก้จาก {claim.declaredAmount.toLocaleString('th-TH')} บาท ({declaredPoints} คะแนน)
                </p>
              )}
              {amended && !decided && (
                <label className="flex w-full flex-col gap-[var(--space-1)]">
                  <span className="text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-[var(--ink-900)]">
                    เหตุผลที่แก้ยอด <span className="text-[var(--error-text)]">จำเป็น</span>
                  </span>
                  <textarea
                    value={amendReason}
                    onChange={(e) => setAmendReason(e.target.value)}
                    rows={2}
                    maxLength={1000}
                    placeholder="เช่น ยอดในสลิปคือ 1,150 บาท ลูกค้ากรอกเกิน"
                    className="w-full rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] p-[var(--space-2)] font-[family-name:var(--font-thai)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
                  />
                  <span className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">
                    เก็บในประวัติการตรวจของคำขอนี้คู่กับยอดก่อนและหลังแก้ — ลูกค้าไม่เห็น
                  </span>
                </label>
              )}
            </div>
          </section>

          <section aria-label="ประวัติของสมาชิก" className="rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-3)]">
            <h3 className="mb-[var(--space-2)] flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--ink-700)]">
              ประวัติของสมาชิก
              <a
                href={`/customers/${claim.member.id}`}
                className="text-[length:var(--text-table-sm)] font-[var(--weight-regular)] text-[var(--blue-700)] underline underline-offset-2 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
              >
                เปิด Customer 360
              </a>
            </h3>
            {/*
              ⚠️ ระดับสมาชิก คะแนนคงเหลือ และสถิติการส่งคำขอ **ไม่ได้มากับคำขอ**
                 adapter เติม Silver / 0 / 0 ครั้ง / ผ่าน 0% ให้ครบชนิด
                 ซึ่งอ่านแล้วเหมือนข้อมูลจริงทุกประการ เจ้าหน้าที่อาจปฏิเสธคำขอ
                 เพราะเห็น "ผ่าน 0%" ทั้งที่สมาชิกคนนั้นไม่เคยถูกปฏิเสธเลย
                 จึงซ่อนทิ้งเมื่อแหล่งข้อมูลให้ไม่ได้ แล้วบอกตรง ๆ ว่ายังดูไม่ได้
            */}
            <div className="grid grid-cols-2 gap-[var(--space-3)]">
              <Field label="สมาชิก" hint={claim.member.memberCode}>{claim.member.name}</Field>
              {showMemberStats && (
                <>
                  <Field label="ระดับ" hint={`คะแนนคงเหลือ ${claim.member.points.toLocaleString('th-TH')}`}>{claim.member.tier}</Field>
                  <Field label="ส่งคำขอมาแล้ว" hint={`อนุมัติ ${claim.member.claimsApproved} · ปฏิเสธ ${claim.member.claimsRejected} (ผ่าน ${rate}%)`}>
                    <span className="bo-num" style={{ textAlign: 'left' }}>{claim.member.claimsTotal} ครั้ง</span>
                  </Field>
                  <Field label="ส่งใน 24 ชม.ล่าสุด" hint={claim.member.claimsToday >= 5 ? 'ถี่กว่าค่าปกติของกลุ่ม' : undefined}>
                    <span className="bo-num" style={{ textAlign: 'left' }}>{claim.member.claimsToday} ครั้ง</span>
                  </Field>
                </>
              )}
            </div>
            {!showMemberStats && (
              <p className="m-0 mt-[var(--space-2)] flex items-start gap-1 text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                <Icon name="info" size={14} />
                ระดับสมาชิก คะแนนคงเหลือ และสถิติการส่งคำขอยังดูที่นี่ไม่ได้ในเฟสนี้
              </p>
            )}
          </section>

          <section aria-label="ประวัติของคำขอนี้" className="rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-3)]">
            <h3 className="mb-[var(--space-2)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--ink-700)]">ประวัติของคำขอนี้</h3>
            <AuditTrail compact entries={auditEntries} />
          </section>
        </div>
      </div>

      {decided ? (
        <div className="border-t border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-4)]">
          <p className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form-lg)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
            <Icon name={claim.status === 'approved' ? 'check_circle' : 'cancel'} size={20} filled className={claim.status === 'approved' ? 'text-[var(--success-text)]' : 'text-[var(--error-text)]'} />
            คำขอนี้ตัดสินแล้วเมื่อ {formatThaiDate((claim.decision?.at ?? '').slice(0, 10))}
            {claim.decision?.reasonLabel && (
              <span className="text-[var(--ink-600)]">
                — {claim.decision.reasonLabel}{claim.decision.detail ? ` — ${claim.decision.detail}` : ''}
              </span>
            )}
          </p>
        </div>
      ) : (
        <DecisionPanel
          busy={busy}
          reasons={REJECT_REASONS}
          warning={
            duplicate ? (
              <>ระบบพบสลิปที่เหมือนกับคำขอ <strong>{duplicate.relatedClaimCode}</strong> ที่อนุมัติไปแล้ว — เปิดเทียบภาพก่อนอนุมัติ</>
            ) : undefined
          }
          approveConsequence={
            <>
              ลูกค้าจะได้รับ <strong>{points.toLocaleString('th-TH')} คะแนน</strong> ทันที
              {amended && ' (คำนวณจากยอดที่แก้ไขแล้ว)'} และยอดสะสมในรอบปีจะเพิ่ม {amount.toLocaleString('th-TH')} บาท
            </>
          }
          onApprove={() => onApprove(claim, amount, amended ? amendReason.trim() : undefined)}
          approveBlockedReason={amended && amendReason.trim().length < AMEND_REASON_MIN
            ? `แก้ยอดแล้ว — กรอกเหตุผลที่แก้ยอดอย่างน้อย ${AMEND_REASON_MIN} ตัวอักษรก่อนอนุมัติ`
            : undefined}
          onReject={(payload) => onReject(claim, payload)}
        />
      )}
    </div>
  );
};

/* ------------------------------------------------------------------- หน้าจอ */

export interface ClaimQueueScreenProps {
  /** สำหรับทดสอบ 4 สถานะของหน้า */
  initialState?: ScreenState;
  /** ค่าเริ่มต้นของตัวกรองผู้รับผิดชอบ — หน้าแรกของเจ้าหน้าที่ตรวจสอบเข้ามาที่ 'me' */
  initialAssignee?: string;
  /**
   * ข้อมูลจากชั้น API
   * ถ้าไม่ส่งมา หน้าจอจะใช้ข้อมูลจำลองในไฟล์ตัวเอง — ใช้ตอนดูหน้านี้ใน /foundations
   * โดยไม่ต้องมีชั้น API มาเกี่ยวข้อง
   */
  claims?: Claim[];
  /**
   * แจ้งชั้น API เมื่อมีการตัดสิน
   * หน้าจออัปเดตตัวเองทันทีแบบ optimistic เพื่อให้ "1 รายการ = การกด 1 ครั้ง" ยังจริง
   * แล้วค่อยรอผลจาก server — ถ้าไม่สำเร็จจะคืนค่าเดิมพร้อมแจ้งเตือน
   */
  onDecide?: (claimId: string, next: QueueStatus, extra: { amount?: number; reasonCode?: string; reasonLabel?: string; detail?: string; comment?: string }) => Promise<void>;
  /** แจ้งชั้น API เมื่อกด "เลิกทำ" */
  onUndo?: (claimId: string) => Promise<void>;
  /**
   * เรียกเมื่อกดปุ่มรีเฟรช — ไม่ส่งมาจะไม่แสดงปุ่ม
   * จำเป็นเมื่อต่อ backend จริง เพราะเจ้าหน้าที่หลายคนทำงานคิวเดียวกัน
   * ของที่คนอื่นตัดสินไปแล้วจะไม่ขึ้นจนกว่าจะโหลดใหม่
   */
  onRefresh?: () => void;
  /** true ระหว่างกำลังโหลดใหม่ — ใช้ปิดปุ่มและแสดงสถานะ */
  refreshing?: boolean;
  /**
   * ความสามารถของแหล่งข้อมูลที่ต่ออยู่
   *
   * ⚠️ ค่าเริ่มต้นคือ "มีครบ" ซึ่งจริงกับข้อมูลจำลอง
   *    แต่ CRM ของ HIS ไม่มีหลายอย่าง — ถ้าไม่ปิด หน้าจอจะแสดงคอลัมน์และตัวกรอง
   *    ที่ว่างเปล่าทุกแถว ซึ่งดูเหมือนข้อมูลหายมากกว่าจะเข้าใจว่าไม่มีฟีเจอร์นี้
   */
  capabilities?: {
    /** มีระดับสมาชิก คะแนนคงเหลือ และสถิติการส่งคำขอของสมาชิก */
    memberStats?: boolean;
    /** มีฟิลด์ผู้รับผิดชอบ */
    assignee?: boolean;
    /** ระบบตรวจจับความผิดปกติ (สลิปซ้ำ ยอดผิดปกติ) */
    anomalies?: boolean;
    /** แยกที่มาเป็น ระบบตรวจอัตโนมัติ / ต้องใช้คนตรวจ */
    source?: boolean;
    /** อนุมัติหลายรายการได้ในคำสั่งเดียว */
    bulkApprove?: boolean;
    /** มีประวัติการแก้ไขจาก backend */
    auditTrail?: boolean;
  };
  /**
   * สูตรคำนวณคะแนนจากยอดเงิน
   *
   * ⚠️ ต้องส่งมาเมื่อต่อ backend จริง เพราะอัตราแลกเปลี่ยนอยู่ที่ earning policy
   *    ของแต่ละ division ไม่ใช่ค่าคงที่ในโค้ด
   *    ค่าตั้งต้น (100 บาท = 1 คะแนน) ใช้ได้เฉพาะกับข้อมูลจำลอง
   *
   *    เจอจริง: ต่อ CRM ที่ตั้งไว้ 1 บาท = 1 คะแนน แล้วหน้าจอยังโชว์ 15 คะแนน
   *    ทั้งที่ของจริงต้องเป็น 1,500 — ผิด 100 เท่าในตัวเลขที่เจ้าหน้าที่ใช้ตัดสิน
   */
  pointsForAmount?: (amount: number) => number;
  /**
   * ส่งเงื่อนไขค้นหาไปกรองฝั่ง server
   *
   * เรียกแบบหน่วงเวลาเมื่อคำค้นหรือช่วงยอดเงินเปลี่ยน ไม่เรียกทุกตัวอักษร
   * ตัวกรองในเครื่องยังทำงานเหมือนเดิม — ตัวนี้แค่**ขยายชุดข้อมูล**ที่ดึงมา
   * ไม่ได้มาแทนที่ ถ้าไม่ส่ง prop นี้มา หน้าจอกรองในเครื่องอย่างเดียวเหมือนเดิม
   */
  onServerFilter?: (f: { query: string; amountMin: string; amountMax: string }) => void;
  /**
   * ประวัติการตรวจของจริงจากชั้นข้อมูล คีย์เป็น id ของคำขอ
   *
   * ไม่ส่งมา = ใช้ประวัติที่ประกอบจากข้อมูลจำลอง
   * ส่งมาแต่ไม่มีคีย์ของคำขอนั้น = คำขอนั้นยังไม่มีประวัติ แสดงว่าง (ไม่ใช่ fallback ไปข้อมูลจำลอง
   * ซึ่งจะกลายเป็นการแสดงประวัติปลอมปนกับของจริง)
   */
  auditByClaimId?: Map<string, AuditEntry[]>;
  /**
   * เรียกเมื่อเจ้าหน้าที่เปิดดูคำขอหนึ่งรายการ
   *
   * มีไว้ให้ชั้น API โหลดข้อมูลสมาชิกเพิ่มเฉพาะรายการที่เปิดจริง
   * เพราะรายการในคิวฝังมาแค่ id กับชื่อ ส่วนระดับสมาชิกและคะแนนคงเหลือ
   * ต้องยิง loyalty-account แยกอีกครั้ง — ยิงล่วงหน้าทุกแถวคือยิงหลายร้อยครั้งโดยเปล่าประโยชน์
   *
   * หน้าจอไม่รอผลลัพธ์ ชั้น API อัปเดต claims เข้ามาเองเมื่อได้ข้อมูล
   */
  onOpenClaim?: (claim: Claim) => void;
}

export const ClaimQueueScreen: React.FC<ClaimQueueScreenProps> = ({
  initialState = 'ready',
  initialAssignee = CURRENT_STAFF_ID,
  claims: claimsProp,
  onDecide,
  onUndo,
  onRefresh,
  refreshing = false,
  capabilities,
  pointsForAmount,
  onServerFilter,
  auditByClaimId,
  onOpenClaim,
}) => {
  const toast = useToast();

  /* ค่าเริ่มต้น = มีครบ (จริงกับข้อมูลจำลอง) */
  const can = {
    assignee: capabilities?.assignee ?? true,
    anomalies: capabilities?.anomalies ?? true,
    source: capabilities?.source ?? true,
    bulkApprove: capabilities?.bulkApprove ?? true,
    auditTrail: capabilities?.auditTrail ?? true,
    memberStats: capabilities?.memberStats ?? true,
  };

  /** ใช้สูตรจาก backend ถ้ามี ไม่งั้นใช้ค่าคงที่ของข้อมูลจำลอง
      ห่อด้วย useCallback เพื่อให้ reference นิ่ง ไม่ทำให้ useMemo ที่อ้างถึงคำนวณใหม่ทุกรอบ */
  const calcPoints = useCallback(
    (amount: number) => (pointsForAmount ?? pointsFor)(amount),
    [pointsForAmount],
  );
  /** ประวัติของจริงถ้ามี ไม่งั้นประกอบจากข้อมูลจำลอง */
  const auditOf = useCallback(
    (c: Claim): AuditEntry[] =>
      auditByClaimId ? (auditByClaimId.get(c.id) ?? []) : (auditFor(c) as AuditEntry[]),
    [auditByClaimId],
  );

  const [screenState, setScreenState] = useState<ScreenState>(initialState);
  const [claims, setClaims] = useState<Claim[]>(claimsProp ?? CLAIMS);

  /* ข้อมูลจากชั้น API มาทีหลัง (โหลด async) จึงต้องรับค่าใหม่เมื่อมันมาถึง
     รวมถึงสถานะหน้าจอด้วย — ไม่งั้นตารางค้างอยู่ที่ "กำลังโหลด" ตลอดไป
     เพราะ useState ใช้ค่าที่ส่งมาแค่รอบแรกรอบเดียว (เจอจริงตอนต่อชั้น API) */
  useEffect(() => { if (claimsProp) setClaims(claimsProp); }, [claimsProp]);
  useEffect(() => { setScreenState(initialState); }, [initialState]);
  const [busy, setBusy] = useState(false);

  const [tab, setTab] = useState<StatusTab>('pending');
  const [query, setQuery] = useState('');
  const [channels, setChannels] = useState<Channel[]>([]);
  const [range, setRange] = useState<DateRange>({ from: null, to: null });
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [assignee, setAssignee] = useState<string>(initialAssignee);
  const [source, setSource] = useState<'all' | 'manual' | 'auto' | 'failed'>('all');

  /**
   * ส่งเงื่อนไขไปให้ชั้นข้อมูลกรองฝั่ง server
   *
   * หน่วง 400ms เพื่อไม่ให้ยิงทุกตัวอักษร — พิมพ์ "สมชาย" จะยิงครั้งเดียวไม่ใช่หกครั้ง
   * ref ทำให้ callback ที่ parent สร้างใหม่ทุก render ไม่ทำให้ตัวจับเวลาเริ่มใหม่
   */
  const serverFilterRef = useRef(onServerFilter);
  serverFilterRef.current = onServerFilter;
  useEffect(() => {
    if (!serverFilterRef.current) return;
    const t = setTimeout(() => {
      serverFilterRef.current?.({ query, amountMin, amountMax });
    }, 400);
    return () => clearTimeout(t);
  }, [query, amountMin, amountMax]);

  /** ตัวเลือกช่องทาง — จากข้อมูลจริง มาร์เก็ตเพลสก่อน ที่เหลือเรียงตามตัวอักษรไทย */
  const channelOptions = useMemo(() => {
    const found = new Set(claims.map((c) => c.channel).filter(Boolean));
    const known = CHANNEL_ORDER.filter((c) => found.has(c));
    const rest = [...found].filter((c) => !CHANNEL_ORDER.includes(c)).sort((a, b) => a.localeCompare(b, 'th'));
    return [...known, ...rest];
  }, [claims]);

  /** เรียงตามอายุคำขอเป็นค่าเริ่มต้น — งานเก่าสุดขึ้นก่อน */
  const [sort, setSort] = useState<SortState | null>({ columnId: 'age', direction: 'desc' });
  const [density, setDensity] = useTableDensity();
  const [selected, setSelected] = useState<string[]>([]);
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [auditOpen, setAuditOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  /** ความคืบหน้าของการอนุมัติแบบกลุ่ม — null = ไม่ได้กำลังทำ */
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number; failed: number } | null>(null);
  const [visibleCols, setVisibleCols] = useState<string[]>([]);

  /* ---------------------------------------------------------- ตัวกรอง */

  const filtered = useMemo(() => {
    const min = amountMin ? Number(amountMin) : null;
    const max = amountMax ? Number(amountMax) : null;
    return claims.filter((c) => {
      if (tab === 'pending' && !(c.status === 'pending' || c.status === 'review' || c.status === 'overdue')) return false;
      if (tab === 'approved' && c.status !== 'approved') return false;
      if (tab === 'rejected' && c.status !== 'rejected') return false;
      if (source !== 'all' && c.source !== source) return false;
      if (channels.length && !channels.includes(c.channel)) return false;
      if (assignee !== 'all' && c.assigneeId !== assignee) return false;
      if (min !== null && c.declaredAmount < min) return false;
      if (max !== null && c.declaredAmount > max) return false;
      if (range.from && c.submittedAt.slice(0, 10) < range.from) return false;
      if (range.to && c.submittedAt.slice(0, 10) > range.to) return false;
      if (query) {
        const q = query.trim();
        if (!c.code.includes(q) && !c.member.name.includes(q) && !(c.orderNumber ?? '').includes(q) && !c.member.memberCode.includes(q)) return false;
      }
      return true;
    });
  }, [claims, tab, source, channels, assignee, amountMin, amountMax, range, query]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = (c: Claim): string | number => {
      switch (sort.columnId) {
        case 'age': return c.ageHours;
        case 'amount': return c.declaredAmount;
        case 'points': return calcPoints(c.verifiedAmount ?? c.declaredAmount);
        case 'member': return c.member.name;
        case 'channel': return c.channel;
        case 'status': return c.status;
        default: return c.code;
      }
    };
    return [...filtered].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      if (typeof ka === 'number' && typeof kb === 'number') return (ka - kb) * dir;
      return String(ka).localeCompare(String(kb), 'th') * dir;
    });
  }, [filtered, sort, calcPoints]);

  const rows = useMemo(() => sorted.slice((page - 1) * pageSize, page * pageSize), [sorted, page, pageSize]);
  const active = useMemo(() => claims.find((c) => c.id === activeId) ?? null, [claims, activeId]);

  /** ภาพของรายการถัดไป — โหลดล่วงหน้าเพื่อให้กดถัดไปแล้วเห็นภาพทันที */
  const nextSlipUrl = useMemo(() => {
    if (!activeId) return undefined;
    const at = sorted.findIndex((c) => c.id === activeId);
    return sorted[at + 1]?.slipUrl;
  }, [sorted, activeId]);

  const counts = useMemo(() => ({
    pending: claims.filter((c) => c.status === 'pending' || c.status === 'review' || c.status === 'overdue').length,
    approved: claims.filter((c) => c.status === 'approved').length,
    rejected: claims.filter((c) => c.status === 'rejected').length,
    all: claims.length,
  }), [claims]);

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const out: ActiveFilter[] = [];
    if (source !== 'all') {
      if (can.source) out.push({ id: 'source', field: 'ที่มา', display: source === 'manual' ? 'ต้องใช้คนตรวจ' : source === 'auto' ? 'ระบบตรวจอัตโนมัติ' : 'ระบบตรวจไม่ได้' });
    }
    if (channels.length) out.push({ id: 'channel', field: 'ช่องทาง', display: channels.join(', ') });
    if (range.from || range.to) out.push({ id: 'range', field: 'ช่วงวันที่', display: `${formatThaiDate(range.from)} – ${formatThaiDate(range.to)}` });
    if (amountMin || amountMax) out.push({ id: 'amount', field: 'ช่วงยอดเงิน', display: `${amountMin || '0'} – ${amountMax || 'ไม่จำกัด'} บาท` });
    if (assignee !== 'all') {
      if (can.assignee) out.push({ id: 'assignee', field: 'ผู้รับผิดชอบ', display: assignee === CURRENT_STAFF_ID ? 'ของฉัน' : (STAFF.find((s) => s.id === assignee)?.name ?? assignee) });
    }
    return out;
  }, [source, channels, range, amountMin, amountMax, assignee, can.assignee, can.source]);

  const clearFilter = (id: string) => {
    if (id === 'source') setSource('all');
    if (id === 'channel') setChannels([]);
    if (id === 'range') setRange({ from: null, to: null });
    if (id === 'amount') { setAmountMin(''); setAmountMax(''); }
    if (id === 'assignee') setAssignee('all');
    setPage(1);
  };

  const clearAll = () => {
    setSource('all'); setChannels([]); setRange({ from: null, to: null });
    setAmountMin(''); setAmountMax(''); setAssignee('all'); setQuery(''); setPage(1);
  };

  /* -------------------------------------------------- การตัดสินและการเลื่อน */

  /** ตัดสินแล้วเลื่อนไปรายการถัดไปในคิวอัตโนมัติ */
  const advance = useCallback((fromId: string) => {
    const pool = sorted.filter((c) => c.status === 'pending' || c.status === 'review' || c.status === 'overdue');
    const at = pool.findIndex((c) => c.id === fromId);
    const next = pool[at + 1] ?? pool[at - 1] ?? null;
    setActiveId(next?.id ?? null);
    setFocusedKey(next?.id ?? null);
  }, [sorted]);

  const decide = useCallback((claim: Claim, next: QueueStatus, extra: Partial<Claim['decision']> & { amount?: number; comment?: string }) => {
    setBusy(true);
    const before = claims;
    setClaims((prev) => prev.map((c) => (c.id === claim.id ? {
      ...c,
      status: next,
      verifiedAmount: extra.amount ?? c.verifiedAmount,
      decision: {
        at: new Date().toISOString(),
        byId: CURRENT_STAFF_ID,
        pointsGranted: next === 'approved' ? calcPoints(extra.amount ?? c.declaredAmount) : 0,
        reasonCode: extra.reasonCode,
        reasonLabel: extra.reasonLabel,
        detail: extra.detail,
      },
    } : c)));
    setBusy(false);
    advance(claim.id);

    /* ส่งให้ server ทีหลัง — ถ้าล้มเหลวคืนค่าเดิมและบอกให้ชัดว่ายังไม่ถูกบันทึก */
    void onDecide?.(claim.id, next, extra).catch((err: unknown) => {
      setClaims(before);
      toast.show({
        tone: 'error',
        message: `บันทึก ${claim.code} ไม่สำเร็จ — ${err instanceof Error ? err.message : 'กรุณาลองใหม่'} รายการถูกคืนกลับเป็นรอตรวจสอบแล้ว`,
      });
    });

    toast.show({
      tone: next === 'approved' ? 'success' : 'info',
      message:
        next === 'approved'
          ? `อนุมัติ ${claim.code} — ให้ ${calcPoints(extra.amount ?? claim.declaredAmount).toLocaleString('th-TH')} คะแนน`
          : `ปฏิเสธ ${claim.code} — แจ้งเหตุผลให้ลูกค้าแล้ว`,
      undo: {
        onUndo: () => { setClaims(before); void onUndo?.(claim.id); },
        seconds: 8,
      },
    });
  }, [claims, advance, toast, onDecide, onUndo, calcPoints]);

  const openRow = useCallback((c: Claim) => {
    setActiveId(c.id);
    setFocusedKey(c.id);
    /* บอกชั้น API ให้โหลดข้อมูลสมาชิกของรายการนี้ — ไม่รอผล ไม่บล็อกการเปิด */
    onOpenClaim?.(c);
  }, [onOpenClaim]);

  /* ------------------------------------------------------------ คอลัมน์ */

  const allColumns: Array<Column<Claim>> = useMemo(() => [
    {
      id: 'slip',
      header: 'สลิป',
      width: 64,
      align: 'center',
      pinned: true,
      value: (c) => (c.kind === 'slip' ? 'มีสลิป' : 'Order ID'),
      cell: (c) =>
        c.slipThumbUrl ? (
          <img src={c.slipThumbUrl} alt={`รูปย่อสลิปของ ${c.code}`} className="h-[28px] w-[22px] rounded-[3px] border border-[var(--line-200)] object-cover" />
        ) : (
          <Icon name="receipt_long" size={18} label={`คำขอจากเลขคำสั่งซื้อ ${c.orderNumber}`} className="text-[var(--ink-400)]" />
        ),
    },
    { id: 'code', header: 'รหัสคำขอ', width: 122, pinned: true, required: true, sortable: true, value: (c) => c.code },
    { id: 'member', header: 'สมาชิก', width: 176, sortable: true, value: (c) => c.member.name },
    { id: 'channel', header: 'ช่องทาง', width: 116, sortable: true, value: (c) => c.channel },
    {
      id: 'amount', header: 'ยอดที่กรอก (บาท)', width: 138, numeric: true, sortable: true,
      value: (c) => c.declaredAmount,
      cell: (c) => (
        <span>
          {c.declaredAmount.toLocaleString('th-TH')}
          {c.verifiedAmount !== undefined && c.verifiedAmount !== c.declaredAmount && (
            <span className="ml-1 text-[var(--warning-text)]">→ {c.verifiedAmount.toLocaleString('th-TH')}</span>
          )}
        </span>
      ),
    },
    {
      id: 'points', header: 'คะแนนที่จะได้', width: 122, numeric: true, sortable: true,
      value: (c) => calcPoints(c.verifiedAmount ?? c.declaredAmount),
      cell: (c) => calcPoints(c.verifiedAmount ?? c.declaredAmount).toLocaleString('th-TH'),
    },
    {
      id: 'age', header: 'อายุคำขอ', width: 168, sortable: true,
      value: (c) => c.ageHours,
      cell: (c) => (
        <span className="flex items-center gap-[var(--space-1)]">
          <span className="bo-num text-[length:var(--text-table-sm)]" style={{ textAlign: 'left' }}>{ageText(c.ageHours)}</span>
          <SlaTag ageHours={c.ageHours} />
        </span>
      ),
    },
    { id: 'status', header: 'สถานะ', width: 150, sortable: true, value: (c) => c.status, cell: (c) => <StatusBadge status={c.status} /> },
    { id: 'source', header: 'ที่มา', width: 180, value: (c) => c.source, cell: (c) => <SourceBadge source={c.source} /> },
    {
      id: 'flags', header: 'ระบบตรวจพบ', width: 128, align: 'center',
      value: (c) => (c.anomalies.length ? `${c.anomalies.length} เรื่อง` : ''),
      cell: (c) =>
        c.anomalies.length ? (
          <span className="inline-flex items-center gap-1 text-[var(--warning-text)]">
            <Icon name="flag" size={14} filled />
            {c.anomalies.length} เรื่อง
          </span>
        ) : <span className="text-[var(--ink-400)]">—</span>,
    },
    { id: 'submittedAt', header: 'วันที่ส่ง', width: 118, sortable: true, value: (c) => formatThaiDate(c.submittedAt.slice(0, 10)) },
    { id: 'assignee', header: 'ผู้รับผิดชอบ', width: 148, value: (c) => STAFF.find((s) => s.id === c.assigneeId)?.name ?? 'ยังไม่มอบหมาย' },
  ], [calcPoints]);


  /**
   * ซ่อนคอลัมน์ที่แหล่งข้อมูลปัจจุบันไม่มีให้
   *
   * ⚠️ ถ้าไม่ซ่อน คอลัมน์จะว่างเปล่าทุกแถว ซึ่งเจ้าหน้าที่อ่านว่า "ข้อมูลหาย"
   *    มากกว่าจะเข้าใจว่า "ระบบยังไม่มีฟีเจอร์นี้"
   */
  const columns = useMemo(
    () => allColumns.filter((c) => {
      if (c.id === 'assignee' && !can.assignee) return false;
      if (c.id === 'flags' && !can.anomalies) return false;
      if (c.id === 'source' && !can.source) return false;
      return true;
    }),
    [allColumns, can.assignee, can.anomalies, can.source],
  );

  const visible = visibleCols.length ? visibleCols : columns.map((c) => c.id);

  /** ตารางฝั่งซ้ายแคบ — แสดงเฉพาะคอลัมน์ที่จำเป็นเมื่อเปิดรายละเอียด */
  const listColumns = active ? columns.filter((c) => ['slip', 'code', 'member', 'amount', 'age', 'status'].includes(c.id)) : columns.filter((c) => visible.includes(c.id));

  const doExport = (format: 'csv' | 'excel') => {
    const n = exportRows({ columns, rows: sorted, columnIds: visible, filename: exportFilename('คิวคำขอสะสมคะแนน'), format });
    /* การส่งออกถูกบันทึกใน audit log ตาม FR-ADM-39 */
    toast.show({ tone: 'success', message: `ส่งออก ${n.toLocaleString('th-TH')} รายการเป็น ${format === 'csv' ? 'CSV' : 'Excel'} แล้ว` });
  };

  /* คำขอที่อนุมัติแบบกลุ่มได้: ระบบตรวจให้แล้ว หรือยอดต่ำกว่าเพดานและไม่มีสิ่งที่ระบบจับได้ */
  const bulkEligible = useMemo(
    () => selected
      .map((id) => claims.find((c) => c.id === id)!)
      .filter((c) => c && (c.status === 'pending' || c.status === 'overdue' || c.status === 'review'))
      .filter((c) => c.source === 'auto' || (c.declaredAmount <= 2000 && c.anomalies.length === 0)),
    [selected, claims],
  );

  /**
   * อนุมัติหลายรายการ — **ยิงทีละรายการจริง**
   *
   * ⚠️ backend ยังไม่มี endpoint อนุมัติแบบกลุ่ม (เอกสารวิเคราะห์ช่องว่างของ API ที่ทีมเก็บไว้ภายใน ข้อ 3.5)
   *    เดิมหน้านี้อัปเดตแค่ state ในเครื่องแล้วขึ้น toast ว่าสำเร็จ
   *    ซึ่งอันตรายมาก เพราะโหลดหน้าใหม่แล้วทุกอย่างกลับมาเป็น "รอตรวจสอบ"
   *    โดยไม่มี error ให้เห็นเลย
   *
   * ทำทีละรายการตามลำดับ ไม่ยิงพร้อมกัน เพื่อไม่ให้ backend รับภาระพุ่ง
   * และเพื่อให้รู้ชัดว่ารายการไหนล้มเหลว
   *
   * รายการที่ล้มเหลวจะถูกคืนสถานะเดิมและรายงานจำนวนให้เห็น
   */
  const runBulkApprove = useCallback(async (reason: string) => {
    const targets = bulkEligible;
    if (!targets.length) return;

    setBulkConfirm(false);
    setBulkProgress({ done: 0, total: targets.length, failed: 0 });

    const succeeded: string[] = [];
    const failed: Array<{ code: string; reason: string }> = [];

    for (let i = 0; i < targets.length; i += 1) {
      const c = targets[i]!;
      const amount = c.verifiedAmount ?? c.declaredAmount;
      try {
        /* เหตุผลที่กรอกในกล่องยืนยัน ไปอยู่ในประวัติการตรวจของทุกรายการ */
        if (onDecide) await onDecide(c.id, 'approved', { amount, comment: reason });
        succeeded.push(c.id);
      } catch (err) {
        failed.push({ code: c.code, reason: err instanceof Error ? err.message : 'ไม่ทราบสาเหตุ' });
      }
      setBulkProgress({ done: i + 1, total: targets.length, failed: failed.length });
    }

    /* อัปเดตเฉพาะรายการที่สำเร็จจริง — ไม่แตะรายการที่ล้มเหลว */
    const okIds = new Set(succeeded);
    setClaims((prev) => prev.map((c) => (okIds.has(c.id) ? {
      ...c,
      status: 'approved' as QueueStatus,
      decision: {
        at: new Date().toISOString(),
        byId: CURRENT_STAFF_ID,
        pointsGranted: calcPoints(c.verifiedAmount ?? c.declaredAmount),
        detail: reason,
      },
    } : c)));

    setSelected(failed.length ? targets.filter((c) => !okIds.has(c.id)).map((c) => c.id) : []);
    setBulkProgress(null);

    if (failed.length === 0) {
      toast.show({
        tone: 'success',
        message: `อนุมัติแล้ว ${succeeded.length} รายการ`,
      });
    } else {
      toast.show({
        tone: 'error',
        message: `อนุมัติสำเร็จ ${succeeded.length} รายการ · ไม่สำเร็จ ${failed.length} รายการ `
          + `(${failed.slice(0, 3).map((f) => f.code).join(', ')}${failed.length > 3 ? ' และอื่น ๆ' : ''}) `
          + `— รายการที่ไม่สำเร็จยังถูกเลือกไว้ให้ลองใหม่`,
      });
    }
  }, [bulkEligible, onDecide, calcPoints, toast]);

  const emptyState =
    activeFilters.length > 0 || query ? (
      <EmptyState kind="no-results" title="ไม่พบคำขอที่ตรงกับตัวกรอง" description="ลองล้างตัวกรองบางตัว หรือขยายช่วงวันที่" action={{ label: 'ล้างตัวกรองทั้งหมด', onClick: clearAll }} />
    ) : tab === 'pending' ? (
      <EmptyState kind="queue-clear" title="ไม่มีคำขอค้างในคิว" description="คำขอที่มอบหมายให้คุณตรวจเสร็จทั้งหมดแล้ว" />
    ) : (
      <EmptyState kind="no-data" title="ยังไม่มีคำขอในสถานะนี้" />
    );

  const table = (
    <DataTable<Claim>
      columns={listColumns}
      rows={rows}
      rowKey={(c) => c.id}
      caption={`คิวคำขอสะสมคะแนน — ${sorted.length} รายการ เรียงตามอายุคำขอ`}
      state={screenState}
      rowCountHint={Math.min(pageSize, 14)}
      errorMessage="โหลดคิวไม่สำเร็จ — เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ข้อมูลที่เห็นอาจไม่เป็นปัจจุบัน"
      onRetry={() => setScreenState('ready')}
      emptyState={emptyState}
      selectable
      selectedKeys={selected}
      onSelectionChange={setSelected}
      sort={sort}
      onSortChange={setSort}
      focusedKey={focusedKey}
      onFocusedKeyChange={setFocusedKey}
      onOpenRow={openRow}
      activeKey={activeId}
      density={density}
      persistKey="claims-queue"
      isFlagged={(c) => c.anomalies.some((a) => a.kind === 'duplicate-slip')}
      className="min-h-0 flex-1 rounded-none border-0"
    />
  );

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-0)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="คิวคำขอสะสมคะแนน"
        count={sorted.length}
        center={<SearchInput value={query} onChange={(v) => { setQuery(v); setPage(1); }} focusOnSlash placeholder="ค้นหารหัสคำขอ ชื่อสมาชิก หรือเลขคำสั่งซื้อ" width={340} />}
        end={
          <>
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing}
                aria-label="โหลดคิวใหม่"
                title="โหลดคิวใหม่ — ดึงรายการล่าสุดจากระบบ"
                className="inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-15)] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-25)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] disabled:text-[var(--text-disabled)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
              >
                <Icon name="refresh" size={16} className={refreshing ? 'animate-spin' : undefined} />
                {refreshing ? 'กำลังโหลด…' : 'โหลดใหม่'}
              </button>
            )}
            <DensityToggle value={density} onChange={setDensity} />
            <ColumnPicker columns={columns} persistKey="claims-queue" value={visible} onChange={setVisibleCols} />
            <div className="flex items-center gap-[var(--space-05)] rounded-[var(--radius-sm)] border border-[var(--line-300)] p-[var(--space-05)]">
              <button
                type="button"
                onClick={() => doExport('excel')}
                className="inline-flex min-h-[var(--hit-icon-sm)] items-center gap-1 rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
              >
                <Icon name="download" size={16} />
                Excel
              </button>
              <button
                type="button"
                onClick={() => doExport('csv')}
                className="inline-flex min-h-[var(--hit-icon-sm)] items-center rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
              >
                CSV
              </button>
            </div>
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              aria-label="คีย์ลัด (?)"
              className="inline-flex h-[var(--hit-control)] w-[var(--hit-control)] items-center justify-center rounded-[var(--radius-sm)] border border-[var(--line-300)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
            >
              <Icon name="keyboard" size={18} />
            </button>
          </>
        }
      />

      {/* แท็บสถานะ + ตัวกรองที่มา (สลิป / ระบบตรวจอัตโนมัติ / ระบบตรวจไม่ได้) */}
      <div className="flex flex-wrap items-end gap-[var(--space-4)] border-b border-[var(--line-200)] px-[var(--space-4)]">
        <div role="tablist" aria-label="สถานะคำขอ" className="flex items-end gap-[var(--space-1)]">
          {([['pending', 'รอตรวจสอบ'], ['approved', 'อนุมัติแล้ว'], ['rejected', 'ปฏิเสธ'], ['all', 'ทั้งหมด']] as Array<[StatusTab, string]>).map(([id, label]) => {
            const on = tab === id;
            return (
              <button
                key={id}
                role="tab"
                type="button"
                aria-selected={on}
                onClick={() => { setTab(id); setPage(1); setSelected([]); }}
                className={`inline-flex min-h-[var(--hit-primary)] items-center gap-[var(--space-15)] border-b-2 px-[var(--space-3)] text-[length:var(--text-form-lg)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-1 ${
                  on ? 'border-[var(--blue-700)] font-[var(--weight-semibold)] text-[var(--blue-700)]' : 'border-transparent text-[var(--ink-700)] hover:border-[var(--line-300)] hover:text-[var(--ink-900)]'
                }`}
              >
                {label}
                <span className={`bo-num rounded-[var(--radius-pill)] px-[var(--space-15)] text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] ${on ? 'bg-[var(--blue-700)] text-white' : 'bg-[var(--surface-100)] text-[var(--ink-700)]'}`}>
                  {counts[id].toLocaleString('th-TH')}
                </span>
              </button>
            );
          })}
        </div>

{can.source && (
        <label className="mb-[var(--space-2)] flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          ที่มา
          <select
            value={source}
            onChange={(e) => { setSource(e.target.value as typeof source); setPage(1); }}
            className="min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
          >
            <option value="all">ทั้งหมด</option>
            <option value="manual">ต้องใช้คนตรวจ (สลิป)</option>
            <option value="auto">ระบบตรวจอัตโนมัติแล้ว</option>
            <option value="failed">ระบบตรวจไม่ได้ — ต้องตรวจเอง</option>
          </select>
        </label>
        )}
      </div>

      <FilterBar
        active={activeFilters}
        onRemove={clearFilter}
        onClearAll={clearAll}
        currentFilters={{ tab, source, channels, range, amountMin, amountMax, assignee }}
        persistKey="claims-queue"
        resultCount={sorted.length}
      >
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          ช่องทาง
          <select
            multiple
            size={1}
            value={channels}
            onChange={(e) => { setChannels([...e.target.selectedOptions].map((o) => o.value as Channel)); setPage(1); }}
            className="min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
          >
            {channelOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        <DateRangePicker value={range} onChange={(r) => { setRange(r); setPage(1); }} />

        <span className="flex items-center gap-[var(--space-1)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          ยอดเงิน
          <input
            type="number"
            value={amountMin}
            onChange={(e) => { setAmountMin(e.target.value); setPage(1); }}
            aria-label="ยอดเงินต่ำสุด"
            placeholder="ต่ำสุด"
            className="bo-num min-h-[var(--hit-control)] w-[96px] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-2)] text-[length:var(--text-form)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
          />
          –
          <input
            type="number"
            value={amountMax}
            onChange={(e) => { setAmountMax(e.target.value); setPage(1); }}
            aria-label="ยอดเงินสูงสุด"
            placeholder="สูงสุด"
            className="bo-num min-h-[var(--hit-control)] w-[96px] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-2)] text-[length:var(--text-form)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
          />
        </span>

          {can.assignee && (
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          ผู้รับผิดชอบ
          <select
            value={assignee}
            onChange={(e) => { setAssignee(e.target.value); setPage(1); }}
            className="min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
          >
            <option value={CURRENT_STAFF_ID}>ของฉัน</option>
            <option value="all">ทุกคน</option>
            {STAFF.filter((s) => s.id !== CURRENT_STAFF_ID).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
          )}
      </FilterBar>

      {active ? (
        <SplitView
          persistKey="claims-review"
          hasDetail
          list={
            <>
              {table}
              <Pagination page={page} pageSize={pageSize} totalItems={sorted.length} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} pageSizeOptions={[50, 100, 200]} />
            </>
          }
          detail={
            <ClaimDetail
              calcPoints={calcPoints}
              onClose={() => { setActiveId(null); setFocusedKey(null); }}
              showAudit={can.auditTrail}
              showSource={can.source}
              showMemberStats={can.memberStats}
              claim={active}
              nextSlipUrl={nextSlipUrl}
              busy={busy}
              onOpenAudit={() => setAuditOpen(true)}
              auditEntries={auditOf(active)}
              onApprove={(c, amt, comment) => decide(c, 'approved', { amount: amt, comment })}
              onReject={(c, p) => decide(c, 'rejected', { reasonCode: p.code, reasonLabel: p.label, detail: p.detail })}
            />
          }
        />
      ) : (
        <>
          {table}
          <BulkActionBar
            selectedKeys={selected}
            totalMatching={sorted.length}
            onSelectAllMatching={() => setSelected(sorted.map((c) => c.id))}
            onClear={() => setSelected([])}
            actions={[
              {
                id: 'bulk-approve',
                label: `อนุมัติแบบกลุ่ม (${bulkEligible.length} จาก ${selected.length} รายการทำได้)`,
                icon: 'done_all',
                tone: 'primary',
                disabled: bulkEligible.length === 0,
                consequence: 'อนุมัติได้เฉพาะรายการที่ระบบตรวจให้แล้ว หรือยอดไม่เกิน 2,000 บาทและไม่มีสิ่งที่ระบบจับได้',
                onRun: () => setBulkConfirm(true),
              },
              { id: 'assign', label: 'มอบหมายให้ฉัน', icon: 'person_add', onRun: () => toast.show({ tone: 'success', message: `รับงาน ${selected.length} รายการมาที่คิวของคุณแล้ว` }) },
              { id: 'export-sel', label: 'ส่งออกที่เลือก', icon: 'download', onRun: () => doExport('excel') },
            ]}
          />
          <Pagination page={page} pageSize={pageSize} totalItems={sorted.length} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} pageSizeOptions={[50, 100, 200]} />
        </>
      )}

      <SideDrawer open={auditOpen} onClose={() => setAuditOpen(false)} title="ประวัติการกระทำทั้งหมด" subtitle={active?.code} width="lg">
        {active && <AuditTrail entries={auditOf(active)} />}
      </SideDrawer>

      {/* ความคืบหน้าการอนุมัติแบบกลุ่ม
          ต้องมีเพราะยิงทีละรายการ ถ้ามี 50 รายการจะใช้เวลาหลายวินาที
          ถ้าไม่บอกอะไรเลย เจ้าหน้าที่จะคิดว่าค้างแล้วกดซ้ำ */}
      {bulkProgress && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="bulk-progress-title"
          className="fixed inset-0 z-[var(--z-modal)] grid place-items-center bg-[var(--scrim)] p-[var(--space-6)]"
        >
          <div className="w-full max-w-[420px] rounded-[var(--radius-xl)] bg-[var(--surface-0)] p-[var(--space-6)] shadow-[var(--elevation-modal)]">
            <h2
              id="bulk-progress-title"
              className="text-[length:var(--text-section)] font-[var(--weight-semibold)] leading-[var(--leading-heading)] text-[var(--ink-900)]"
            >
              กำลังอนุมัติทีละรายการ
            </h2>
            <p className="mt-[var(--space-2)] text-[length:var(--text-form)] leading-[var(--leading-form)] text-[var(--ink-700)]">
              ระบบส่งทีละคำขอเพื่อให้ตรวจสอบย้อนหลังได้ครบทุกใบ
              <br />
              กรุณาอย่าปิดหน้านี้จนกว่าจะเสร็จ
            </p>

            <p className="mt-[var(--space-4)] text-[length:var(--text-metric)] font-[var(--weight-bold)] leading-[var(--leading-num)] text-[var(--blue-700)]">
              <span className="bo-num" aria-live="polite">
                {bulkProgress.done} / {bulkProgress.total}
              </span>
            </p>

            {/* แถบความคืบหน้า — สื่อด้วยตัวเลขกำกับเสมอ ไม่ใช่แถบอย่างเดียว */}
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={bulkProgress.total}
              aria-valuenow={bulkProgress.done}
              className="mt-[var(--space-2)] h-[8px] w-full overflow-hidden rounded-[var(--radius-pill)] bg-[var(--line-200)]"
            >
              <div
                className="h-full rounded-[var(--radius-pill)] bg-[var(--blue-700)] transition-[width]"
                style={{ width: `${Math.round((bulkProgress.done / bulkProgress.total) * 100)}%` }}
              />
            </div>

            {bulkProgress.failed > 0 && (
              <p className="mt-[var(--space-3)] flex items-center gap-[var(--space-1)] text-[length:var(--text-form)] text-[var(--error-text)]">
                <Icon name="error" size={17} filled />
                ไม่สำเร็จแล้ว <span className="bo-num">{bulkProgress.failed}</span> รายการ
              </p>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={bulkConfirm}
        tone="default"
        title={`อนุมัติ ${bulkEligible.length} คำขอพร้อมกัน`}
        description={
          <>
            <p className="mb-[var(--space-2)]">
              จะให้คะแนนรวม{' '}
              <strong className="bo-num">
                {bulkEligible.reduce((s, c) => s + calcPoints(c.verifiedAmount ?? c.declaredAmount), 0).toLocaleString('th-TH')}
              </strong>{' '}
              คะแนน แก่สมาชิก {new Set(bulkEligible.map((c) => c.member.id)).size} คน
            </p>
            <p className="text-[length:var(--text-form)] text-[var(--ink-600)]">
              {selected.length - bulkEligible.length > 0 &&
                `อีก ${selected.length - bulkEligible.length} รายการอนุมัติแบบกลุ่มไม่ได้ เพราะยอดสูงหรือมีสิ่งที่ระบบจับได้ ต้องเปิดดูภาพสลิปก่อน`}
            </p>
          </>
        }
        confirmLabel="อนุมัติทั้งหมด"
        requireReason
        reasonLabel="เหตุผลที่อนุมัติแบบกลุ่ม"
        reasonPresets={['ระบบตรวจกับแพลตฟอร์มผ่านทั้งหมด', 'ยอดต่ำและช่องทางเชื่อถือได้', 'เคลียร์งานค้างที่เกิน SLA']}
        busy={bulkProgress !== null}
        onConfirm={(reason) => { void runBulkApprove(reason ?? ''); }}
        onCancel={() => setBulkConfirm(false)}
      />

      {/* ⚠️ ต้อง mount ไว้ตลอด ห้ามใส่ {helpOpen && …} ครอบ
          เพราะคีย์ลัด `?` ถูกผูกอยู่ข้างใน KeyboardShortcutHelp เอง
          ถ้า mount เฉพาะตอนเปิด คีย์ `?` จะไม่มีใครรับ และหน้านี้จะเปิดได้ทางปุ่มเดียว
          (ตัว component ซ่อนตัวเองอยู่แล้วเมื่อ open = false) */}
      <KeyboardShortcutHelp
        open={helpOpen}
        onOpenChange={setHelpOpen}
        shortcuts={[
          ...QUEUE_SHORTCUTS,
          { key: '+', description: 'ซูมภาพสลิปเข้า', group: 'ดูรูปสลิป' },
          { key: '-', description: 'ซูมภาพสลิปออก', group: 'ดูรูปสลิป' },
          { key: '0', description: 'พอดีหน้าจอ', group: 'ดูรูปสลิป' },
          { key: '1', description: 'ขนาดจริง 100%', group: 'ดูรูปสลิป' },
        ]}
      />
    </div>
  );
};

export default ClaimQueueScreen;

/* ============================================================================
   รองรับ:
   FR-ADM-10  คิวตรวจสอบคำขอสะสมคะแนนจากสลิป — รายการ ตัวกรอง ภาพสลิป การตัดสิน
   FR-ADM-11  คำขอจากเลขคำสั่งซื้อ — อยู่คิวเดียวกัน แยกด้วยตัวกรอง "ที่มา"
              พร้อมปุ่มคัดลอกเลขและลิงก์เปิดหน้าแพลตฟอร์มสำหรับช่วงที่ยังไม่เชื่อม API
   FR-ADM-12  การจับรายการซ้ำ — กล่องเตือน + ปุ่มเทียบภาพสองรายการข้างกัน
   FR-ADM-13  อนุมัติแบบกลุ่ม — จำกัดเฉพาะรายการที่ปลอดภัย และมีหน้ายืนยันที่บอกคะแนนรวม
   FR-ADM-14  SLA — ป้ายอายุคำขอเป็นข้อความทุกแถว และเรียงตามอายุเป็นค่าเริ่มต้น
   FR-ADM-04  การแก้ยอดเงินที่ใช้คำนวณคะแนน ต้องบันทึกเหตุผลและเข้า Audit Log
   FR-ADM-09  การปฏิเสธต้องเลือกเหตุผล และข้อความที่ส่งลูกค้าต้องบอกวิธีแก้ไขได้
   FR-ADM-37  ทุกการกระทำเข้า Audit Log — แสดงย่อในแผงรายละเอียด และเต็มใน SideDrawer
   FR-ADM-39  การส่งออกข้อมูลถูกบันทึกและแจ้งผู้ใช้ว่ามีการบันทึก
   ประเด็นค้าง: ข้อ ค (คำสัญญา SLA เมื่อเชื่อม API), ข้อ ง (เพดานคำขอต่อวัน 5 หรือ 10),
   ข้อ ช (เพดานยอดของ Bulk Approve — ใช้ 2,000 บาทเป็นค่าตั้งต้น รอยืนยัน),
   ข้อ ฏ (การมอบหมายงานและการล็อกรายการที่กำลังตรวจ)
   ============================================================================ */
